import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { API } from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Trash2, LayoutGrid, ArrowRight, Image } from "lucide-react";

export default function Boards() {
  const { t, user } = useApp();
  const isViewer = user?.role === "viewer";
  const [boards, setBoards] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const bgRef = React.useRef(null);
  const [bgFor, setBgFor] = useState(null);

  const uploadBg = async (file) => {
    if (!file || !bgFor) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/boards/${bgFor}/background`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("msg.saved"));
      load();
    } catch {
      toast.error(t("msg.error"));
    } finally {
      setBgFor(null);
      if (bgRef.current) bgRef.current.value = "";
    }
  };

  const load = useCallback(() => {
    api.get("/boards").then((r) => setBoards(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/boards", { name });
      toast.success(t("msg.saved"));
      setOpen(false);
      setName("");
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/boards/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="boards-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <LayoutGrid className="h-7 w-7 text-primary" />{t("nav.boards")}
        </h1>
        {!isViewer && (
          <Button onClick={() => setOpen(true)} data-testid="new-board-button">
            <Plus className="h-4 w-4 mr-2" />{t("boards.new")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {boards.map((b) => (
          <Card key={b.id} className="group hover:border-primary/50 transition-colors relative overflow-hidden" data-testid={`board-card-${b.id}`}>
            {b.bg_path && (
              <>
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${API}/files/download?path=${encodeURIComponent(b.bg_path)})` }} />
                <div className="absolute inset-0 bg-black/55" />
              </>
            )}
            <CardHeader className="relative flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">{b.name}</CardTitle>
              {!isViewer && <div className="flex gap-0.5">
                <Button size="icon" variant="ghost" title={t("boards.setBg")}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`board-bg-${b.id}`}
                  onClick={() => { setBgFor(b.id); setTimeout(() => bgRef.current?.click(), 0); }}>
                  <Image className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-board-${b.id}`} onClick={() => remove(b.id)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>}
            </CardHeader>
            <CardContent className="relative flex items-center justify-between">
              <Badge variant="secondary" data-testid={`board-count-${b.id}`}>
                {b.task_count} {t("boards.tasks")}
              </Badge>
              <Link to={`/boards/${b.id}`} data-testid={`open-board-${b.id}`}>
                <Button size="sm" variant="ghost">
                  {t("boards.open")}<ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      <input ref={bgRef} type="file" accept="image/*" className="hidden"
        data-testid="board-bg-file-input" onChange={(e) => uploadBg(e.target.files[0])} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("boards.new")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("boards.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="board-name-input" />
            </div>
            <Button className="w-full" onClick={create} data-testid="board-create-button">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
